/* eslint-disable react/no-danger */
import React from 'react';
import cx from 'classnames';
import { FixedSizeList } from 'react-window';
import { is } from 'immutable';
import { updateStyle, scrollBarWidth } from '@/lib/domHelpers';

import { throttleCancelable } from '@/lib/helpers';
import { setUiSelectOpenAction } from '@/domain/ui';
import { isSuggestedGroupBoundary } from '@/domain/journal/helper';

import { compose } from 'redux';
import { connect } from 'react-redux';

import OptionAddNew from '@/components/Tables/Cells/ListCell/options/OptionAddNew';
import HighlightText from '@/components/mui/HighlightText';
import IconButton from '@mui/material/IconButton';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import CircularProgress from '@mui/material/CircularProgress';
import OptionHint from '@/components/Tables/Cells/ListCell/options/OptionHint';
import {
  OptionsPaper,
  OptionRow,
  OptionTextRuler,
  OptionText,
} from '@/components/Tables/Cells/ListCell/options/StyledComponents';

import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';

const DEFAULT_ITEMS_PER_PAGE = 10;

class Options extends React.Component {
  maxContentWidth = 0;

  optionsEl = {};

  container;

  body;

  constructor(props) {
    super(props);

    this.state = {
      height: 0,
      style: {},
      visibleStartIndex: 0,
    };
  }

  componentDidMount() {
    const { isDisabledScroll, inputEl, setUiSelectOpen } = this.props;
    this.body = document.querySelector('[data-element="content.document.tableBlock"]');
    if (this.body) {
      updateStyle(this.body, [{ name: 'padding-right', value: scrollBarWidth() }]);
    }

    if (!isDisabledScroll) {
      updateStyle(this.body, [{ name: 'overflow', value: 'hidden' }]);
      setUiSelectOpen(true);
    }

    this.setContentWidth(inputEl);
    this.initPosition();
  }

  shouldComponentUpdate(nextProps, nextState) {
    const { term, optionList, index, isLoading, minWidth } = this.props;
    const { directionUP, visibleStartIndex } = this.state;
    if (term !== nextProps.term) {
      this.resetContentWidth(nextProps.inputEl);
      return true;
    }

    if (term !== nextProps.term) return true;
    if (index !== nextProps.index) return true;
    if (minWidth !== nextProps.minWidth) return true;
    if (!is(optionList, nextProps.optionList)) return true;
    if (isLoading !== nextProps.isLoading) return true;

    if (nextState.directionUP !== directionUP) return true;
    return nextState.visibleStartIndex !== visibleStartIndex;
  }

  componentWillUnmount() {
    const { isDisabledScroll, setUiSelectOpen } = this.props;
    if (this.body) {
      updateStyle(this.body, [{ name: 'padding-right', value: null }]);
    }

    if (!isDisabledScroll) {
      updateStyle(this.body, [{ name: 'overflow', value: null }]);
      setUiSelectOpen(false);
    }
    this.onMouseMove.cancel();
  }

  onMouseMove = throttleCancelable(20, (index) => {
    const { setActive } = this.props;
    setActive(this.vListKeyToDataKey(index));
  });

  /* onMouseMove = (index: number) => {
    const { setActive } = this.props;
    setActive(this.vListKeyToDataKey(index));
  } */

  onListRender = ({ visibleStartIndex, visibleStopIndex }) => {
    const { onRenderItems = (x) => x } = this.props;
    onRenderItems(visibleStartIndex, visibleStopIndex);
    this.setState({ visibleStartIndex });
    this.initPosition();
    this.forceUpdate();
  };

  onClickEdit = (e, item) => {
    const { onClickEdit } = this.props;
    e.preventDefault();
    e.stopPropagation();
    onClickEdit(item);
  };

  getValue(value) {
    const { term } = this.props;
    return <HighlightText value={value} searchTerm={term} component="span" />;
  }

  getDataList = () => {
    const { optionList } = this.props;
    return this.isShiftList() ? optionList.unshift(null) : optionList;
  };

  getWidth = () => {
    const minWidth = this.isShiftList() ? 200 : 0;
    // Add extra width for checkbox icon when multiSelect is enabled
    // CheckIcon (small) + margin + buffer = ~25px
    const multiSelectPadding = this.props.multiSelect ? 25 : 0;
    return Math.min(Math.max(this.maxContentWidth + multiSelectPadding, minWidth), window.innerWidth - 50);
  };

  getItemCounts = () => {
    const { isLoading } = this.props;
    const getDateList = this.getDataList();
    return isLoading ? getDateList.size + 1 : getDateList.size;
  };

  getActiveText = () => {
    const data = this.getDataList();
    const activeIndex = this.getActiveIndex();
    return activeIndex >= 0 && data && data.get(activeIndex) ? data.get(activeIndex).display : null;
  };

  getActiveEl = () => this.optionsEl[this.getActiveIndex()];

  getHintProps = () => {
    const optionEl = this.getActiveEl();
    const text = this.getActiveText();
    const width = this.getWidth();

    return optionEl && text && optionEl.offsetWidth > width ? { optionEl, text, width } : null;
  };

  getActiveIndex = () => {
    const { index } = this.props;
    return this.isShiftList() ? index + 1 : index;
  };

  isActiveIndex = (index) => index === this.getActiveIndex();

  getPageItemCount = () => Math.min(this.getAutoHeightCount(), this.getItemCounts());

  getAutoHeightCount = () => {
    const { itemHeight, pageItemsCount } = this.props;
    const { height } = this.state;
    return height && itemHeight ? Math.floor(height / itemHeight) : pageItemsCount || DEFAULT_ITEMS_PER_PAGE;
  };

  setOptionsElement = (el, index, isEditable = false) => {
    this.optionsEl[index] = el;
    const increase = isEditable ? 10 : 0;
    this.setContentWidth(el, increase);
  };

  resetContentWidth = (el) => {
    this.maxContentWidth = 0;
    this.setContentWidth(el);
    this.forceUpdate();
  };

  setContentWidth = (el, increase = 0) => {
    if (el) {
      const width = el.offsetWidth;
      this.maxContentWidth = width > this.maxContentWidth ? width + increase : this.maxContentWidth;
    }
  };

  vListKeyToDataKey = (key) => {
    const subtract = this.isShiftList() ? 1 : 0;
    return key - subtract;
  };

  initPosition = () => {
    const { inputEl } = this.props;
    if (inputEl) {
      const { top, height, left } = inputEl.getBoundingClientRect();
      const width = this.getWidth();
      const { innerHeight } = window;
      const center = Math.floor(top + height / 2);
      const isDirectionUP = innerHeight / center < 2;
      const maxHeight = isDirectionUP ? top : innerHeight - center;
      const overlap = width + left + 5 - window.innerWidth;
      const currentLeft = overlap > 0 ? left - overlap : left;
      const style = isDirectionUP
        ? {
            bottom: innerHeight - top,
            left: currentLeft,
          }
        : {
            top: top + height,
            left: currentLeft,
          };

      const margin = 120;
      this.setState({ height: maxHeight - margin, style });
    }
  };

  isShiftList = () => {
    const { addNewItem } = this.props;
    return !!addNewItem;
  };

  refContainer = (el) => {
    this.container = el;
    this.forceUpdate();
  };

  renderBaseItem = ({ index, data, style }) => {
    const { doSelect } = this.props;
    const isActive = this.isActiveIndex(index);
    const itemData = data.get(index);
    const isSelected = itemData ? itemData.selected : false;

    return itemData ? (
      <OptionRow
        className={cx({ 'je-option-group-boundary': isSuggestedGroupBoundary(data, index) })}
        style={style}
      >
        <ListItemButton
          selected={isActive}
          disabled={!itemData.isActive || itemData.readonly}
          dense
          onMouseDown={(e) => {
            e.preventDefault();
          }}
          sx={{
            direction: 'ltr',
            paddingRight: '28px',
            ...(isSelected && {
              '&&': {
                backgroundColor: 'action.selected',
              },
              '&:hover': { backgroundColor: 'action.selected' },
            }),
          }}
          onMouseMove={() => this.onMouseMove(index)}
          onMouseEnter={() => this.onMouseMove(index)}
          onClick={() => doSelect(this.vListKeyToDataKey(index))}
        >
          {this.props.multiSelect && (
            <CheckIcon
              fontSize="small"
              sx={{
                mr: 0.5,
                visibility: isSelected ? 'visible' : 'hidden',
                color: 'primary.main',
                flexShrink: 0,
              }}
            />
          )}
          <OptionTextRuler ref={(el) => this.setOptionsElement(el, index, itemData.isEditable)}>
            {itemData.display}
          </OptionTextRuler>
          <OptionText>{this.getValue(itemData.display)}</OptionText>
        </ListItemButton>
        {itemData.isEditable && (
          <IconButton
            color="primary"
            sx={{ position: 'absolute', top: 1, right: 0 }}
            onClick={(e) => this.onClickEdit(e, itemData)}
          >
            <EditIcon />
          </IconButton>
        )}
      </OptionRow>
    ) : (
      <ListItem component="div" sx={{ direction: 'ltr' }} style={style}>
        <CircularProgress size={12} />
      </ListItem>
    );
  };

  renderItem = (params) => {
    const { addNewItem } = this.props;
    const { index, style } = params;
    const isAddNew = this.isShiftList() && index === 0;
    return isAddNew ? <OptionAddNew onClick={addNewItem} style={style} /> : this.renderBaseItem(params);
  };

  render() {
    const { style, visibleStartIndex } = this.state;
    const { itemHeight, setListRef, index, isLoading } = this.props;

    const itemCounts = this.getItemCounts();
    const hintProps = this.getHintProps();

    return (
      <OptionsPaper style={style} ref={this.refContainer}>
        {this.container && (itemCounts || isLoading) && itemHeight && (
          <>
            {/* TODO: JE OptionHint not work correctly - rework in future */}
            {hintProps && <OptionHint key={visibleStartIndex} {...hintProps} />}
            <FixedSizeList
              className="list"
              height={itemHeight * this.getPageItemCount()}
              itemCount={itemCounts}
              itemSize={itemHeight}
              width={this.getWidth()}
              itemData={this.getDataList()}
              onItemsRendered={this.onListRender}
              ref={setListRef}
              activeIndex={index}
            >
              {this.renderItem}
            </FixedSizeList>
          </>
        )}
      </OptionsPaper>
    );
  }
}

const mapDispatchToProps = {
  setUiSelectOpen: setUiSelectOpenAction,
};

export default compose(connect(null, mapDispatchToProps))(Options);
